import { EventAction } from 'utils/analytics/amplitudeEventConstants';
import { StoreType } from '@tock/shared/utils/connect';
import {
  allowAmplitudeTracking,
  amplitudeSendEvent,
  amplitudeSetUserId,
  amplitudeSetUserProperty,
  amplitudeSetUserPropertyOnce,
  revokeAmplitudeTracking,
  setupAmplitudeTracking,
} from 'utils/analytics/amplitude';
import { appPatronSelector, getActiveAuthSelector } from 'selectors/appSelectors';
import { flexSelector } from 'selectors/featuresSelector';
import { useConsentRestrictedScripts } from 'hooks/useConsentRestrictedScripts';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useMountEffect } from '@tock/hooks';
import { useSelector } from 'react-redux';
import AppSettings from 'settings/AppSettings';
import analytics from 'utils/Analytics';
import platform from 'platform';

/**
 * Configures Amplitude SDK for use in Dashboard and opt's in to tracking
 * based on user's consent level.
 */
export const Amplitude: React.FunctionComponent<React.PropsWithChildren<unknown>> = () => {
  const { consentLevelFunctional, scriptAdded, setScriptAdded } = useConsentRestrictedScripts();
  const amplitudeKey = AppSettings.AMPLITUDE_KEY;

  useMountEffect(() => {
    const onInitComplete = () => {
      setScriptAdded(true);
    };

    setupAmplitudeTracking(amplitudeKey, onInitComplete);
  });

  useEffect(() => {
    if (!scriptAdded) {
      return;
    }
    if (consentLevelFunctional) {
      allowAmplitudeTracking();
    } else {
      revokeAmplitudeTracking();
    }
  }, [consentLevelFunctional, scriptAdded]);

  return scriptAdded ? <InitializeAmplitude /> : null;
};

const sendPageview = () => {
  amplitudeSendEvent(EventAction.ACTION_VIEW_PAGE);
};

/**
 * Set Tock specific user properties to include with every Amplitude event.
 * This is a component so we can conditionally include it once after Amplitude
 * has been setup which avoids checking for scriptAdded when setting each property.
 *
 * @see https://help.amplitude.com/hc/en-us/articles/115002380567-User-properties-and-event-properties#h_39e46c92-7b7f-4358-a96f-c82cc3342e3e
 */
const InitializeAmplitude = () => {
  const location = useLocation();
  const activeAuth = useSelector(getActiveAuthSelector);
  const { id: patronId, uuid: patronUuid } = useSelector(appPatronSelector);
  const tockRoles = useSelector((state: StoreType) => state.app.authData?.role);
  const isFlexBusiness = useSelector(flexSelector);
  const { mobileModeEnabled } = useSelector((state: StoreType) => state.persistedUi);

  useEffect(() => {
    if (patronUuid) {
      amplitudeSetUserId(patronUuid);
    }
  }, [patronUuid]);

  useEffect(() => {
    if (activeAuth) {
      analytics.setActiveAuthCustomDimensionsAmplitude(activeAuth);
    }
  }, [activeAuth]);

  useEffect(() => {
    if (patronId) {
      amplitudeSetUserPropertyOnce({ property: 'PatronId', value: patronId });
    }
  }, [patronId]);

  useEffect(() => {
    if (tockRoles) {
      amplitudeSetUserProperty({ property: 'Tock role', value: tockRoles });
    }
  }, [tockRoles]);

  useEffect(() => {
    amplitudeSetUserProperty({
      property: 'Business type',
      value: isFlexBusiness ? 'Flex' : 'Slot',
    });
  }, [isFlexBusiness]);

  useEffect(() => {
    platform.name && amplitudeSetUserProperty({ property: 'Browser type', value: platform.name });
    platform.version &&
      amplitudeSetUserProperty({
        property: 'Browser version',
        value: platform.version,
      });
  }, []);

  useEffect(() => {
    amplitudeSetUserProperty({
      property: 'Viewport width',
      value: window.innerWidth,
    });
  }, []);

  useEffect(() => {
    amplitudeSetUserProperty({
      property: 'Viewport height',
      value: window.innerHeight,
    });
  }, []);

  useEffect(() => {
    amplitudeSetUserProperty({
      property: 'Mobile view setting',
      value: mobileModeEnabled,
    });
  }, [mobileModeEnabled]);

  useEffect(() => {
    sendPageview();
  }, [location?.pathname]);

  return null;
};
